import React, { memo, useState, useCallback, useRef, useEffect } from 'react'
import { useTranslation } from 'services/Translation'
import Typography from 'components/common/Typography'
import TextInputComposed from 'components/common/inputs/Text/Composed'
import DefaultBtn from 'components/common/buttons/Default/Component'
import SecondaryBtn from 'components/common/buttons/Secondary'
import Separator from 'components/common/Separator'
import { getClaims } from 'services/Authentication'
import { textPrimary, purple } from 'styles/Colors'
import { CS, CS_m } from 'back-end-api'
import TextInput from 'components/common/inputs/Text'
import Close from 'components/common/buttons/Close/Component'
import UnderlineBtn from 'components/common/buttons/Underline'
import ArrowIcon from 'assets/icons/arrows/Small'
import FloatingCard from 'components/common/Card/Floating'
import { hp } from 'styles/Sizes'
import styled from 'styled-components'
import { usePatientRouteParams } from 'components/patient/Parameters'
import { ALLOWED_IMAGE_TYPES } from 'utils/imageUpload'

import { ModalContainer } from '../../Common'
import TemplatesCard from './TemplatesCard'
import { AdviceStatus } from '../common'

const texts = (getText: (key: string, category: string) => string) => (key: string) =>
  ({
    complaint_title: getText('Complaint', 'Patient_Details_Tabs_Summary_Form'),
    btnLabelClose: getText('Close', 'Patient_Details_Modal_Advice'),
    title: getText('Advice Notification', 'Patient_Details_Modal_Advice'),
    text: getText(
      'Send a medical advice to medically support the sick employee during the absence process, or if you wish to provide medical advice for recovery. Make sure the message is clearly understandable and that the patient is sufficiently informed.',
      'Patient_Details_Modal_Advice'
    ),
    templateLoad: getText('Load templates or, write your own', 'Patient_Details_Modal_Advice'),
    templateTitle: getText('Templates', 'Patient_Details_Modal_Advice'),
    templateCreateBtn: getText('Create new template', 'Patient_Details_Modal_Advice'),
    templateCreateBtnShort: getText('Create as new', 'Patient_Details_Modal_Advice'),
    adviceInputLabel: getText('Please type your advice', 'Patient_Details_Modal_Advice'),
    adviceInputPlaceholder: getText('Write here', 'Patient_Details_Modal_Advice'),
    btnLabelNext: getText('Next', 'Patient_Details_Modal_Advice'),
    btnLabelSaveEdits: getText('Save edits to template', 'Patient_Details_Modal_Advice'),
    btnLabelSavingEdits: getText('Saving...', 'Patient_Details_Modal_Advice'),
    additional_complaint: getText('Additional Complaint', 'Patient_Details_Modal_Advice'),
    general_question: getText('General Medical Question', 'Patient_Details_Modal_Advice'),
    insertPlaceholder: getText('Insert variable', 'Patient_Details_Modal_Advice'),
    placeholderFirstName: getText('First Name', 'Patient_Details_Modal_Advice'),
    placeholderLastName: getText('Last Name', 'Patient_Details_Modal_Advice'),
    placeholderAge: getText('Age', 'Patient_Details_Modal_Advice'),
    placeholderBirthday: getText('Birthday', 'Patient_Details_Modal_Advice'),
    placeholderGender: getText('Gender', 'Patient_Details_Modal_Advice'),
    translateTo: getText('Translate to', 'Patient_Details_Modal_Advice'),
    translating: getText('Translating...', 'Patient_Details_Modal_Advice'),
  }[key])

const PLACEHOLDERS_OPTIONS = [
  { value: '[patient-firstname]', label: 'placeholderFirstName' },
  { value: '[patient-lastname]', label: 'placeholderLastName' },
  { value: '[patient-age]', label: 'placeholderAge' },
  { value: '[patient-birthday]', label: 'placeholderBirthday' },
  { value: '[patient-gender]', label: 'placeholderGender' },
]

const TRANSLATE_LANGUAGE_OPTIONS = [
  { value: 'en', label: 'English' },
  { value: 'nl', label: 'Nederlands' },
  { value: 'pl', label: 'Polski' },
]

const PlaceholderDropdown = styled.div`
  position: relative;
  display: inline-block;
`

const PlaceholderButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  background: #f5f5f5;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  font-size: 13px;
  font-family: coresanscr45;
  color: ${textPrimary};
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #EB7C0C15;
    border-color: #EB7C0C;
  }
`

const PlaceholderMenu = styled.div<{ isOpen: boolean }>`
  position: absolute;
  top: 100%;
  left: 0;
  margin-top: 4px;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 100;
  min-width: 160px;
  display: ${props => (props.isOpen ? 'block' : 'none')};
`

const PlaceholderMenuItem = styled.button`
  display: block;
  width: 100%;
  padding: 10px 14px;
  text-align: left;
  background: none;
  border: none;
  font-size: 14px;
  font-family: coresanscr45;
  color: ${textPrimary};
  cursor: pointer;
  transition: background 0.15s ease;
  
  &:first-child {
    border-radius: 8px 8px 0 0;
  }
  
  &:last-child {
    border-radius: 0 0 8px 8px;
  }
  
  &:hover {
    background: #EB7C0C15;
    color: #EB7C0C;
  }
`

interface WriteAdviceProps {
  onClose: () => void
  formatTemplateText: (text: string) => string
  setStatus: (status: AdviceStatus) => void
  advice: string
  setAdvice: (advice: string) => void
  title: string
  setTitle: (title: string) => void
}

const FooterAdvice = styled.div`
  width: 100%;
  padding-bottom: 24px;
  padding-top: 16px;
  z-index: 0;
`

const ImageAttachmentArea = styled.div<{ isDragOver?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  margin-top: 8px;
  border: 1.5px dashed ${props => (props.isDragOver ? '#EB7C0C' : '#e0e0e0')};
  border-radius: 8px;
  background: ${props => (props.isDragOver ? '#EB7C0C08' : '#fafafa')};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: #EB7C0C;
    background: #EB7C0C08;
  }
`

const ImageAttachmentLabel = styled.span`
  font-size: 13px;
  font-family: coresanscr45;
  color: ${textPrimary};
`

const ImagePreviewList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
`

const ImagePreviewItem = styled.div`
  position: relative;
  width: 64px;
  height: 64px;
  border-radius: 6px;
  overflow: hidden;
  border: 1px solid #e0e0e0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

const ImageRemoveBtn = styled.button`
  position: absolute;
  top: 2px;
  right: 2px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.6);
  border: none;
  color: white;
  font-size: 11px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;

  &:hover {
    background: rgba(0, 0, 0, 0.8);
  }
`

const UploadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  color: #EB7C0C;
  font-family: coresanscr45;
`

const TranslateDropdown = styled.div`
  position: relative;
  display: inline-block;
`

const TranslateButton = styled.button<{ isTranslating?: boolean }>`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  background: ${props => (props.isTranslating ? '#EB7C0C15' : '#f0f4ff')};
  border: 1px solid ${props => (props.isTranslating ? '#EB7C0C' : '#c5d0e6')};
  border-radius: 6px;
  font-size: 13px;
  font-family: coresanscr45;
  color: ${props => (props.isTranslating ? '#EB7C0C' : '#4a6fa5')};
  cursor: ${props => (props.isTranslating ? 'wait' : 'pointer')};
  transition: all 0.2s ease;
  
  &:hover {
    background: #EB7C0C15;
    border-color: #EB7C0C;
    color: #EB7C0C;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const TranslateMenu = styled.div<{ isOpen: boolean }>`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 4px;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 100;
  min-width: 140px;
  display: ${props => (props.isOpen ? 'block' : 'none')};
`

const TranslateMenuItem = styled.button`
  display: block;
  width: 100%;
  padding: 10px 14px;
  text-align: left;
  background: none;
  border: none;
  font-size: 14px;
  font-family: coresanscr45;
  color: ${textPrimary};
  cursor: pointer;
  transition: background 0.15s ease;
  
  &:first-child {
    border-radius: 8px 8px 0 0;
  }
  
  &:last-child {
    border-radius: 0 0 8px 8px;
  }
  
  &:hover {
    background: #EB7C0C15;
    color: #EB7C0C;
  }
`

const WriteAdvice = ({
  onClose,
  formatTemplateText,
  setStatus,
  advice,
  setAdvice,
  title,
  setTitle,
}: WriteAdviceProps) => {
  const { t, lang } = useTranslation(texts)

  const [openTemplateSelection, setOpenTemplateSelection] = useState(false)
  const [isContainerExpanded, setIsContainerExpanded] = useState(false)
  const [openPlaceholderMenu, setOpenPlaceholderMenu] = useState(false)
  const [openTranslateMenu, setOpenTranslateMenu] = useState(false)
  const [isTranslating, setIsTranslating] = useState(false)
  const [attachedImages, setAttachedImages] = useState<{ file: File; preview: string; uploading: boolean; url?: string }[]>([])
  const [isDragOver, setIsDragOver] = useState(false)
  // Track the currently loaded template so we can offer an "edit template" save path.
  // Stores the id and the exact title/text that were loaded, to detect user edits.
  const [loadedTemplate, setLoadedTemplate] = useState<{
    id: number
    originalTitle: string
    originalText: string
  } | null>(null)
  const [savingTemplateEdits, setSavingTemplateEdits] = useState(false)
  const isTranslatingRef = useRef(false)
  const adviceLanguageRef = useRef(lang)
  const adviceRef = useRef(advice)
  const titleRef = useRef(title)
  const templatesFloatingRef = useRef<any>(null)
  const placeholderMenuRef = useRef<HTMLDivElement>(null)
  const translateMenuRef = useRef<HTMLDivElement>(null)
  const editorRef = useRef<any>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Keep refs in sync with latest state
  useEffect(() => { adviceRef.current = advice }, [advice])
  useEffect(() => { titleRef.current = title }, [title])

  const claims = getClaims()
  const res = CS.useAdviceTemplatesList({ doctor_account_id: claims?.sub ?? '' })
  const templates = res.data?.sort((a, b) => (a.title > b.title ? -1 : 1)) ?? []
  const templateTitleAlreadyUsed = res.data?.some(te => te.title === title)

  const { summary } = usePatientRouteParams()
  const complaint = CS.getSummarySumupTranslateIfNone(summary, lang)
  const additionalComplaint = CS.getSummaryPatientCommentTranslateIfNone(summary, lang)

  const deleteTemplate = useCallback(
    async (templateToDelete: CS_m.ModelsAdviceTemplate) => {
      setTitle('')
      setAdvice('')
      setLoadedTemplate(null)
      await CS.deleteAdviceTemplate({ id: templateToDelete.id })
    },
    [setTitle, setAdvice]
  )

  const openCreateTemplateModal = useCallback(
    (newTitle: string, newAdvice: string) => () => {
      setAdvice(newAdvice)
      setTitle(newTitle)
      setLoadedTemplate(null)
      setOpenTemplateSelection(false)
      setStatus(AdviceStatus.CreateAdviceTemplate)
    },
    [setAdvice, setTitle, setOpenTemplateSelection, setStatus]
  )

  const stopUsingTemplate = useCallback(() => {
    setAdvice('')
    setTitle('')
    setLoadedTemplate(null)
  }, [setAdvice, setTitle])

  const selectTemplate = useCallback(
    (newSelectedTemplate: CS_m.ModelsAdviceTemplate | null) => {
      if (newSelectedTemplate) {
        const formattedText = formatTemplateText(newSelectedTemplate.text)
        setAdvice(formattedText)
        setTitle(newSelectedTemplate.title)
        // Remember what was loaded so we can detect edits and offer a save button.
        // We store the formatted text since the editor operates on the formatted value.
        setLoadedTemplate({
          id: newSelectedTemplate.id,
          originalTitle: newSelectedTemplate.title,
          originalText: formattedText,
        })
        adviceLanguageRef.current = lang
      }
    },
    [setAdvice, setTitle, formatTemplateText, lang]
  )

  const saveTemplateEdits = useCallback(async () => {
    if (!loadedTemplate) return
    setSavingTemplateEdits(true)
    try {
      const updateRes = await CS.updateAdviceTemplate({
        id: loadedTemplate.id,
        title,
        text: advice,
      })
      if (!updateRes.error) {
        // Commit the new baseline so the "Save edits" button disappears until
        // the doctor makes further changes.
        setLoadedTemplate({
          id: loadedTemplate.id,
          originalTitle: title,
          originalText: advice,
        })
      }
    } finally {
      setSavingTemplateEdits(false)
    }
  }, [loadedTemplate, title, advice])

  const handleAdviceChange = useCallback(
    (newValue: string) => {
      // Skip onChange events triggered by programmatic translation updates
      // ReactQuill fires onChange when value prop changes, which would re-process
      // the already-translated text through formatTemplateText and potentially mangle it
      if (isTranslatingRef.current) return

      // Apply placeholder replacement in real-time
      setAdvice(formatTemplateText(newValue))
      adviceLanguageRef.current = lang
    },
    [setAdvice, formatTemplateText, lang]
  )

  const insertPlaceholder = useCallback(
    (placeholder: string) => {
      const formattedValue = formatTemplateText(placeholder)
      const quill = editorRef.current?.getEditor?.()

      if (quill) {
        const range = quill.getSelection()
        const position = range ? range.index : quill.getLength() - 1
        quill.insertText(position, formattedValue)
        quill.setSelection(position + formattedValue.length)
      } else {
        // Fallback if editor ref not available
        setAdvice(advice + formattedValue)
      }

      setOpenPlaceholderMenu(false)
    },
    [formatTemplateText, setAdvice, advice]
  )

  const toggleContainerExpansion = useCallback(() => {
    setIsContainerExpanded(prev => !prev)
  }, [])

  const handleTranslate = useCallback(
    async (targetLanguage: string) => {
      const currentAdvice = adviceRef.current
      const currentTitle = titleRef.current
      if (!currentAdvice || currentAdvice === '<p><br></p>' || isTranslatingRef.current) return

      setOpenTranslateMenu(false)
      setIsTranslating(true)
      isTranslatingRef.current = true

      try {
        const fromLang = adviceLanguageRef.current

        // Translate advice and title in parallel to avoid partial translation
        // when one request fails and the other succeeds
        const [translateRes, titleRes] = await Promise.all([
          // @ts-ignore — translateText is not yet in the generated back-end-api clinic-service client; to be added when the clinic-service orval client is regenerated.
          CS.translateText({
            text: currentAdvice,
            from_language: fromLang,
            to_language: targetLanguage,
          }),
          currentTitle
            // @ts-ignore — translateText not yet in generated client
            ? CS.translateText({
                text: currentTitle,
                from_language: fromLang,
                to_language: targetLanguage,
              })
            : Promise.resolve(null),
        ])

        if (!translateRes.error && translateRes.data?.translated_text) {
          setAdvice(translateRes.data.translated_text)
        }

        if (titleRes && !titleRes.error && titleRes.data?.translated_text) {
          setTitle(titleRes.data.translated_text)
        }

        adviceLanguageRef.current = targetLanguage
      } catch (err) {
        console.error('Translation failed:', err)
      } finally {
        // Delay clearing the flag so that any pending Quill onChange events
        // triggered by the setAdvice above are still blocked
        setTimeout(() => {
          isTranslatingRef.current = false
          setIsTranslating(false)
        }, 100)
      }
    },
    [setAdvice, setTitle]
  )

  // Close placeholder menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (placeholderMenuRef.current && !placeholderMenuRef.current.contains(event.target as Node)) {
        setOpenPlaceholderMenu(false)
      }
      if (translateMenuRef.current && !translateMenuRef.current.contains(event.target as Node)) {
        setOpenTranslateMenu(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Upload image to S3 via presigned URL and insert into editor
  const uploadAndInsertImage = useCallback(
    async (file: File) => {
      if (!summary?.id) return

      // Only allow image files.
      if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
        // eslint-disable-next-line no-alert
        alert('Unsupported file type. Please choose a JPEG, PNG, or WebP image.')
        return
      }

      // Backend maximum for raw advice-image uploads: 3,000,000 bytes (~2.86 MiB).
      const MAX_IMAGE_SIZE = 3000000
      if (file.size > MAX_IMAGE_SIZE) {
        console.error(`Image too large: ${file.size} bytes (max ${MAX_IMAGE_SIZE})`)
        // eslint-disable-next-line no-alert
        alert(`Image is too large. Maximum size is ${Math.floor(MAX_IMAGE_SIZE / 1024)}KB.`)
        return
      }

      const extension = (file.name.split('.').pop() || 'png').toLowerCase()

      // Create preview data URL for the attachment thumbnail
      const preview = await new Promise<string>(resolve => {
        const reader = new FileReader()
        reader.onload = () => resolve(reader.result as string)
        reader.readAsDataURL(file)
      })

      // Reserve a slot in attached images for this upload
      let imageIndex = 0
      setAttachedImages(prev => {
        imageIndex = prev.length
        return [...prev, { file, preview, uploading: true }]
      })

      try {
        // Get presigned URL from backend using the raw file size in bytes
        const uploadRes = await CS.setSummaryAdvicePicture({
          id: summary.id,
          picture_extension: extension,
          picture_size: file.size,
        })

        if (uploadRes.error || !uploadRes.data?.presigned_upload_url) {
          setAttachedImages(prev => prev.filter((_, i) => i !== imageIndex))
          // eslint-disable-next-line no-alert
          alert('Could not prepare the image upload. Please try again.')
          return
        }

        // Upload the raw binary (Blob) to S3, not the base64 string.
        // Using axios directly so we can set the correct Content-Type for proper rendering.
        const axios = (await import('axios')).default
        await axios.put(uploadRes.data.presigned_upload_url, file, {
          headers: {
            'Content-Type': file.type || `image/${extension}`,
          },
        })

        // Extract the public URL (presigned URL without query params)
        const publicUrl = uploadRes.data.presigned_upload_url.split('?')[0]

        // Update attached image state with URL
        setAttachedImages(prev =>
          prev.map((img, i) =>
            i === imageIndex ? { ...img, uploading: false, url: publicUrl } : img
          )
        )

        // Insert image into the Quill editor
        const quill = editorRef.current?.getEditor?.()
        if (quill) {
          const range = quill.getSelection() || { index: quill.getLength() - 1 }
          quill.insertEmbed(range.index, 'image', publicUrl)
          quill.setSelection(range.index + 1)
        }
      } catch (err) {
        console.error('Image upload failed:', err)
        setAttachedImages(prev => prev.filter((_, i) => i !== imageIndex))
        // eslint-disable-next-line no-alert
        alert('Image upload failed. Please try again.')
      }
    },
    [summary]
  )

  // Custom image handler for Quill toolbar image button
  const handleQuillImageClick = useCallback(() => {
    fileInputRef.current?.click()
  }, [])

  // Handle file input change (from toolbar button or attachment area)
  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files
      if (!files) return

      Array.from(files).forEach(file => {
        if (file.type.startsWith('image/')) {
          uploadAndInsertImage(file)
        }
      })

      // Reset input so same file can be selected again
      e.target.value = ''
    },
    [uploadAndInsertImage]
  )

  // Handle drag and drop
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
  }, [])

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      setIsDragOver(false)

      const files = e.dataTransfer.files
      Array.from(files).forEach(file => {
        if (file.type.startsWith('image/')) {
          uploadAndInsertImage(file)
        }
      })
    },
    [uploadAndInsertImage]
  )

  // Remove an attached image
  const removeAttachedImage = useCallback(
    (index: number) => {
      const img = attachedImages[index]
      if (img?.url) {
        // Also remove from editor content
        const quill = editorRef.current?.getEditor?.()
        if (quill) {
          const delta = quill.getContents()
          let currentIndex = 0
          delta.ops?.forEach((op: any) => {
            if (op.insert?.image === img.url) {
              quill.deleteText(currentIndex, 1)
            } else {
              currentIndex += typeof op.insert === 'string' ? op.insert.length : 1
            }
          })
        }
      }
      setAttachedImages(prev => prev.filter((_, i) => i !== index))
    },
    [attachedImages]
  )
  return (
    <>
      <ModalContainer label={t('btnLabelClose')} onClose={onClose}>
        <div className="relative">
          <div
            className="flex flex-col text-lg py-3 mb-3 border-b border-grey-100 py-6"
            style={{ 
              height: isContainerExpanded ? 380 : 180, 
              overflow: 'scroll',
              transition: 'height 0.3s ease-in-out'
            }}
          >
          {summary?.complaint?.name && complaint && (
            <>
              <div className="flex flex-col text-lg py-3 mb-3">
                <Typography
                  type="title"
                  className="font-csc65"
                  text={t('complaint_title')}
                  color={textPrimary}
                  size={18}
                />
                <Typography
                  className="mt-3 font-csc45 text-medium"
                  text={summary?.complaint?.name}
                  color={purple}
                  size={15}
                />
                <TextInput
                  field={{
                    value: complaint,
                  }}
                  inputStyle={{ lineHeight: '25px', fontSize: '15px' }}
                  multiline
                  noBorder
                  noPadding
                  readOnly
                  className="mt-3 font-csc45 text-medium"
                  style={undefined}
                  label={undefined}
                  inputRef={undefined}
                  containerStyle={undefined}
                  placeholderStyle={undefined}
                />
              </div>
              {additionalComplaint && (
                <div className="flex flex-col text-lg py-3 mb-3">
                  <Typography
                    type="title"
                    className="font-csc65"
                    text={t('additional_complaint')}
                    color={textPrimary}
                    size={18}
                  />
                  <TextInput
                    field={{
                      value: additionalComplaint,
                    }}
                    inputStyle={{ lineHeight: '25px', fontSize: '15px' }}
                    multiline
                    noBorder
                    noPadding
                    readOnly
                    className="mt-3 font-csc45 text-medium"
                    style={undefined}
                    label={undefined}
                    inputRef={undefined}
                    containerStyle={undefined}
                    placeholderStyle={undefined}
                  />
                </div>
              )}
            </>
          )}
          {!complaint && additionalComplaint && (
            <div className="flex flex-col text-lg py-3 mb-3">
              <Typography
                type="title"
                className="font-csc65"
                text={t('general_question')}
                color={textPrimary}
                size={18}
              />
              <TextInput
                field={{
                  value: additionalComplaint,
                }}
                inputStyle={{ lineHeight: '25px', fontSize: '15px' }}
                multiline
                noBorder
                noPadding
                readOnly
                className="mt-3 font-csc45 text-medium"
                style={undefined}
                label={undefined}
                inputRef={undefined}
                containerStyle={undefined}
                placeholderStyle={undefined}
              />
            </div>
          )}
          </div>
          {/* Expand/Collapse Button */}
          <button
            type="button"
            onClick={toggleContainerExpansion}
            className="absolute bottom-1 left-1/2 transform -translate-x-1/2 bg-white border border-grey-100 rounded px-3 py-1 shadow-sm hover:shadow-md transition-shadow duration-200"
            style={{ 
              color: '#EB7C0C',
              fontSize: '12px',
              fontFamily: 'coresanscr45',
              zIndex: 10
            }}
            title={isContainerExpanded ? 'Collapse' : 'Expand'}
          >
            {isContainerExpanded ? (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="m18 15-6-6-6 6"/>
              </svg>
            ) : (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="m6 9 6 6 6-6"/>
              </svg>
            )}
          </button>
        </div>
        {/* Spacer - reduced to keep Adviesbericht in same position while expanding container above */}
        <div style={{ height: '0px' }} />
        <div className="flex flex-col text-lg py-3 mb-3">
          <Typography
            type="title"
            className="font-csc65"
            text={t('title')}
            color={textPrimary}
            size={18}
          />
          <Typography
            className="mt-3 font-csc45 text-medium"
            text={t('text')}
            color={textPrimary}
            size={15}
          />
        </div>
        <>
          <div className="flex justify-between align-center border-b border-grey-100 py-6">
            <div
              className={`flex flex-wrap justify-start items-center ${
                title && !templateTitleAlreadyUsed ? 'w-80' : 'w-1/2'
              }`}
            >
              <TextInput
                noBorder
                className="font-csc65"
                noPadding
                inputStyle={{ color: textPrimary, fontSize: '1.125rem' }}
                // eslint-disable-next-line no-nested-ternary
                containerStyle={{
                  width: ` ${title ? '60%' : !templateTitleAlreadyUsed ? '100%' : '50%'}`,
                }}
                placeholderStyle={{ fontSize: '1.125rem' }}
                placeholder={t('templateLoad')}
                field={{
                  value: title,
                  onChange: ({ target: { value } }: any) => setTitle(value),
                }}
                style={undefined}
                label={undefined}
                inputRef={undefined}
              />
              {title && templateTitleAlreadyUsed && (
                <Close style={{ height: '16px', width: '16px' }} mini onClick={stopUsingTemplate} />
              )}
            </div>

            {title && !templateTitleAlreadyUsed && (
              <div className="flex justify-center align-center border-l border-grey-100 px-6">
                <UnderlineBtn
                  bold
                  onClick={openCreateTemplateModal(title, advice)}
                  label={t('templateCreateBtnShort')}
                  innerRef={undefined}
                  previousIcon={undefined}
                  nextIcon={undefined}
                  style={undefined}
                />
              </div>
            )}
            <UnderlineBtn
              innerRef={templatesFloatingRef}
              bold
              onClick={() => {
                setOpenTemplateSelection(v => !v)
              }}
              nextIcon={<ArrowIcon color={textPrimary} style={undefined} />}
              label={t('templateTitle')}
              previousIcon={undefined}
              style={undefined}
            />
          </div>

          <FloatingCard
            active={openTemplateSelection}
            setActive={() => {
              setOpenTemplateSelection(v => !v)
            }}
            top={
              templatesFloatingRef?.current &&
              templatesFloatingRef.current.offsetTop +
                templatesFloatingRef.current.offsetHeight -
                24
            }
            left={
              templatesFloatingRef?.current &&
              templatesFloatingRef.current.offsetLeft + templatesFloatingRef.current.offsetWidth
            }
            arrowPos="right"
            paddingHorizontal={undefined}
            paddingVetical={undefined}
            style={undefined}
          >
            <TemplatesCard
              options={[...templates.map(el => el.title)]}
              footerLabel={t('templateCreateBtn')}
              footerCallback={openCreateTemplateModal('', '')}
              onTemplateSelect={(value: number | undefined) =>
                selectTemplate(value ? templates[value - 1] : null)
              }
              onTemplateDelete={(idx: number) => {
                deleteTemplate(templates[idx - 1])
              }}
            />
          </FloatingCard>

          <Separator size={hp(3)} />
          <div className="flex items-center justify-between">
            <Typography
              type="title"
              className="font-csc65"
              text={t('adviceInputLabel')}
              color={textPrimary}
              size={18}
            />
            <div className="flex items-center gap-2">
              <TranslateDropdown ref={translateMenuRef}>
                <TranslateButton
                  type="button"
                  isTranslating={isTranslating}
                  disabled={isTranslating || !advice || advice === '<p><br></p>'}
                  onClick={() => setOpenTranslateMenu(v => !v)}
                >
                  {isTranslating ? t('translating') : t('translateTo')}
                  {!isTranslating && (
                    <ArrowIcon color="#4a6fa5" style={{ transform: openTranslateMenu ? 'rotate(180deg)' : 'none' }} />
                  )}
                </TranslateButton>
                <TranslateMenu isOpen={openTranslateMenu}>
                  {TRANSLATE_LANGUAGE_OPTIONS.map(option => (
                    <TranslateMenuItem
                      key={option.value}
                      type="button"
                      onClick={() => handleTranslate(option.value)}
                    >
                      {option.label}
                    </TranslateMenuItem>
                  ))}
                </TranslateMenu>
              </TranslateDropdown>
              <PlaceholderDropdown ref={placeholderMenuRef}>
                <PlaceholderButton
                  type="button"
                  onClick={() => setOpenPlaceholderMenu(v => !v)}
                >
                  {t('insertPlaceholder')}
                  <ArrowIcon color={textPrimary} style={{ transform: openPlaceholderMenu ? 'rotate(180deg)' : 'none' }} />
                </PlaceholderButton>
                <PlaceholderMenu isOpen={openPlaceholderMenu}>
                  {PLACEHOLDERS_OPTIONS.map(option => (
                    <PlaceholderMenuItem
                      key={option.value}
                      type="button"
                      onClick={() => insertPlaceholder(option.value)}
                    >
                      {t(option.label)}
                    </PlaceholderMenuItem>
                  ))}
                </PlaceholderMenu>
              </PlaceholderDropdown>
            </div>
          </div>
          <Separator size={hp(3)} />
          <TextInputComposed
            ref={editorRef}
            height={430}
            fontSize={13}
            noPadding
            placeholder={t('adviceInputPlaceholder')}
            field={{
              value: advice,
              onChange: handleAdviceChange,
            }}
            imageHandler={handleQuillImageClick}
            data-cy="inputAdvice"
          />

          {/* Hidden file input for image uploads */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            style={{ display: 'none' }}
            onChange={handleFileSelect}
          />

          {/* Image attachment area */}
          <ImageAttachmentArea
            isDragOver={isDragOver}
            onClick={() => fileInputRef.current?.click()}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#EB7C0C" strokeWidth="2">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
              <circle cx="8.5" cy="8.5" r="1.5"/>
              <polyline points="21 15 16 10 5 21"/>
            </svg>
            <ImageAttachmentLabel>
              {isDragOver ? 'Drop image here' : 'Click or drag images to attach'}
            </ImageAttachmentLabel>
          </ImageAttachmentArea>

          {/* Attached images preview */}
          {attachedImages.length > 0 && (
            <ImagePreviewList>
              {attachedImages.map((img, index) => (
                <ImagePreviewItem key={index}>
                  <img src={img.preview} alt={`Attachment ${index + 1}`} />
                  {img.uploading && <UploadingOverlay>...</UploadingOverlay>}
                  {!img.uploading && (
                    <ImageRemoveBtn
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation()
                        removeAttachedImage(index)
                      }}
                    >
                      ×
                    </ImageRemoveBtn>
                  )}
                </ImagePreviewItem>
              ))}
            </ImagePreviewList>
          )}
        </>

        <FooterAdvice>
          <div className="w-full flex justify-center align-center border-t border-grey-100 pt-12">
            {loadedTemplate &&
              (title !== loadedTemplate.originalTitle ||
                advice !== loadedTemplate.originalText) && (
                <>
                  <SecondaryBtn
                    label={savingTemplateEdits ? t('btnLabelSavingEdits') : t('btnLabelSaveEdits')}
                    onClick={saveTemplateEdits}
                    type="button"
                    className="font-csc65"
                    style={{ width: '12.5rem' }}
                    color={textPrimary}
                    disabled={
                      savingTemplateEdits ||
                      !advice ||
                      advice === '<p><br></p>' ||
                      !title
                    }
                    background={undefined}
                    innerRef={undefined}
                  />
                  <Separator type="horizontal" size={hp(3)} />
                </>
              )}
            <DefaultBtn
              label={t('btnLabelNext')}
              onClick={() => {
                setStatus(AdviceStatus.ConfirmAdvice)
              }}
              type="submit"
              className="font-csc65"
              style={{ width: '9.375rem' }}
              color={textPrimary}
              reversedColors
              disabled={!advice || advice === '<p><br></p>' || !title}
              nextIcon={undefined}
            />
          </div>
        </FooterAdvice>
      </ModalContainer>
    </>
  )
}

WriteAdvice.whyDidYouRender = true

export default memo(WriteAdvice)
